import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { RolePill } from "@/components/common";
import AccountSettingsDialog from "@/components/AccountSettingsDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Bell, LogOut, ShieldCheck, LayoutGrid, Database, HardDrive, FolderKanban, Building2,
  BookOpen,
  Menu, ChevronDown, Layers, Users, GitPullRequest, Package, Settings,
} from "lucide-react";
import { LifeBuoy } from "lucide-react";

const NAVS = {
  "/m1": [
    { to: "/m1", label: "Engagements", icon: FolderKanban, exact: true },
    { to: "/m1/configure", label: "Configure", icon: LayoutGrid, perm: "configure" },
    { to: "/m1/help", label: "Help Center", icon: BookOpen },
    { to: "/m1/support", label: "Support & Help", icon: ShieldCheck },
  ],
  "/admin": [
    { to: "/admin", label: "Users & Roles", icon: Building2, exact: true },
    { to: "/admin/branding", label: "Branding", icon: LayoutGrid },
    { to: "/admin/config", label: "Configuration", icon: Layers },
    { to: "/admin/plans", label: "Plans & Requests", icon: Package },
    { to: "/admin/billing", label: "Billing", icon: ShieldCheck },
  ],
  "/platform": [
    { to: "/platform", label: "Overview", icon: LayoutGrid, exact: true },
    { to: "/platform/tenants", label: "Tenants", icon: Building2 },
    { to: "/platform/users", label: "Users", icon: Users },
    { to: "/platform/plans", label: "Plans", icon: Package },
    { to: "/platform/requests", label: "Plan Requests", icon: GitPullRequest },
    { to: "/platform/support", label: "Support Access", icon: ShieldCheck },
    { to: "/platform/support-tickets", label: "Support Tickets", icon: LifeBuoy },
    { to: "/platform/storage", label: "Storage", icon: HardDrive },
    { to: "/platform/audit", label: "Audit Log", icon: Database },
  ],
};

const ACCENTS = {
  "/m1": "#4F46E5", "/admin": "#475569", "/platform": "#D97706",
};

export default function AppShell({ children }) {
  const { session, logout, notifs, refreshNotifs, loadMe } = useAuth();
  const nav = useNavigate();
  const exitSupport = async () => {
    localStorage.removeItem("azx_act_as");
    await loadMe();
    nav("/platform");
  };
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [ticketUnread, setTicketUnread] = useState(0);

  useEffect(() => {
    const seg = "/" + (loc.pathname.split("/")[1] || "m1");
    if (seg !== "/platform") { setTicketUnread(0); return; }
    let alive = true;
    const fetchCount = () => api.get("/platform/support-tickets/unread-count")
      .then((r) => alive && setTicketUnread(r.data.count || 0)).catch(() => {});
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [loc.pathname]);

  if (!session) return null;

  const seg = "/" + (loc.pathname.split("/")[1] || "m1");
  const base = NAVS[seg] ? seg : "/m1";
  const accent = ACCENTS[base] || "#2563EB";
  const isManager = session.roles?.some((r) => ["manager", "tenant_admin"].includes(r));
  const items = (NAVS[base] || []).filter((i) => !(i.perm === "configure" && !isManager));

  const modules = session.available_modules || [];
  const tenant = session.tenant;
  const brandColor = tenant?.branding?.primary || "#2563EB";
  const unread = notifs.filter((n) => !n.read).length;

  const openNotif = async (n) => {
    try { if (!n.read) await api.post(`/notifications/${n.id}/read`); } catch (e) { console.debug("mark notif read failed", e); }
    refreshNotifs?.();
    if (n.link) nav(n.link);
  };
  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); } catch (e) { console.debug("mark all read failed", e); }
    refreshNotifs?.();
  };

  const NavList = ({ onClick }) => (
    <nav className="space-y-1">
      {items.map((it) => {
        const active = it.exact ? loc.pathname === it.to : loc.pathname.startsWith(it.to);
        return (
          <button key={it.to} data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => { nav(it.to); onClick?.(); }}
            className={cn("flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              active ? "text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800")}
            style={active ? { backgroundColor: accent } : {}}>
            <it.icon className="h-4 w-4" />
            {it.label}
            {it.to === "/platform/support-tickets" && ticketUnread > 0 && (
              <span data-testid="ticket-unread-badge"
                className="ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[11px] font-bold text-white">
                {ticketUnread}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-slate-200 dark:border-slate-800 bg-white/85 dark:bg-slate-900/85 backdrop-blur-md">
        <div className="flex h-14 items-center gap-3 px-4 sm:px-6">
          <div className="md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-4">
                <div className="mb-6 mt-2"><Brand tenant={tenant} brandColor={brandColor} /></div>
                <NavList onClick={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
          </div>

          <Brand tenant={tenant} brandColor={brandColor} />

          <div className="ml-auto flex items-center gap-1.5">
            {session.impersonating && (
              <button data-testid="exit-support-btn" onClick={exitSupport}
                className="hidden sm:inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs font-semibold hover:bg-amber-200">
                <ShieldCheck className="h-3 w-3" /> Support session · Exit
              </button>
            )}
            <RolePill role={session.platform_role || session.roles?.[0] || "user"} className="hidden sm:inline-flex" />

            <DropdownMenu onOpenChange={(o) => o && refreshNotifs()}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="h-5 w-5" />
                  {unread > 0 && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
                  {unread > 0 && (
                    <button data-testid="notif-read-all" onClick={markAllRead}
                      className="text-[11px] font-semibold text-indigo-600 hover:underline">Mark all read</button>
                  )}
                </div>
                <DropdownMenuSeparator />
                {notifs.length === 0 && <div className="px-2 py-6 text-center text-sm text-slate-400">No notifications</div>}
                {notifs.slice(0, 10).map((n) => (
                  <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openNotif(n)}
                    className="flex w-full gap-2 items-start px-2 py-2 text-sm text-left rounded hover:bg-slate-50 dark:hover:bg-slate-800">
                    <span className={cn("mt-1 h-1.5 w-1.5 shrink-0 rounded-full", n.read ? "bg-slate-300" : "bg-blue-500")} />
                    <div className="min-w-0">
                      <div className="text-slate-700 dark:text-slate-200">{n.message}</div>
                      <div className="text-[11px] text-slate-400">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  </button>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="profile-menu-btn" className="flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 py-1 pl-1 pr-2 hover:bg-slate-50 dark:hover:bg-slate-800">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: accent }}>
                    {session.user?.name?.[0]?.toUpperCase() || "U"}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{session.user?.name}</div>
                  <div className="text-xs font-normal text-slate-500">{session.user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {session.roles?.includes("tenant_admin") && (
                  <DropdownMenuItem data-testid="menu-admin-console" onClick={() => nav("/admin")}>
                    <Building2 className="mr-2 h-4 w-4" /> Admin Console
                  </DropdownMenuItem>
                )}
                {modules.length > 0 && (
                  <DropdownMenuItem onClick={() => nav("/m1")}>
                    <LayoutGrid className="mr-2 h-4 w-4" /> Workspace
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-account-settings" onClick={() => setSettingsOpen(true)}>
                  <Settings className="mr-2 h-4 w-4" /> Account settings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="mx-auto flex w-full">
        <aside className="hidden md:block w-60 shrink-0 border-r border-slate-200 dark:border-slate-800 p-4 h-[calc(100vh-3.5rem)] sticky top-14">
          <div className="overline mb-3 px-3" style={{ color: accent }}>
            {base === "/m1" ? "Data Collection"
              : base === "/admin" ? "Firm Admin" : "Platform"}
          </div>
          <NavList />
        </aside>
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 azx-in">{children}</main>
      </div>
      <AccountSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} session={session} />
    </div>
  );
}

function Brand({ tenant, brandColor }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="tenant-brand">
      {tenant?.branding?.logo_text ? (
        <span className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-extrabold text-white font-heading" style={{ backgroundColor: brandColor }}>
          {tenant.branding.logo_text}
        </span>
      ) : (
        <img src="/favicon-512.png" alt="AzexPan" className="h-8 w-8 rounded-lg" data-testid="brand-logo" />
      )}
      <div className="leading-tight">
        <div className="font-heading text-sm font-bold text-slate-900 dark:text-white">{tenant?.name || "AzexPan"}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">AzexPan</div>
      </div>
    </div>
  );
}
